/**
 * Runtime-agnostic env reader.
 *
 * @edemics/* packages are consumed by BOTH Vite (tenant-app, super-admin)
 * and Next (landing). Vite exposes env vars via `import.meta.env.VITE_*`
 * (rewritten at build time); Next exposes them via `process.env.NEXT_PUBLIC_*`
 * (also rewritten at build time, but only exists at runtime under
 * server/Next).
 *
 * Vite strips `process.env.X` reads at build time UNLESS explicitly defined —
 * unresolved `process.env.FOO` reads at module load throw `ReferenceError:
 * process is not defined` in the browser.
 *
 * Pattern: try Vite's import.meta.env first, then Node/Next's process.env,
 * then fall back to `undefined`. Never throw.
 *
 * Convention for shared config:
 *   readEnv("API_URL") checks both VITE_API_URL and NEXT_PUBLIC_API_URL.
 *   Prefixes are implied — callers pass the bare name.
 */

type EnvValue = string | undefined

function readViteEnv(key: string): EnvValue {
  try {
    // import.meta.env only exists under Vite; the try/catch swallows the
    // reference error in Node/Next runtimes where it isn't defined.
    const env = typeof import.meta !== "undefined" ? (import.meta as any).env : undefined
    if (env && typeof env === "object") {
      const viteKey = `VITE_${key}`
      const v = env[viteKey]
      if (typeof v === "string") return v
    }
  } catch {
    // import.meta not available in this runtime
  }
  return undefined
}

function readProcessEnv(key: string): EnvValue {
  try {
    if (typeof process !== "undefined" && process.env) {
      const nextKey = `NEXT_PUBLIC_${key}`
      const v = process.env[nextKey]
      if (typeof v === "string") return v
      // Also try the bare key (server-only vars, or non-Next Node use)
      const bare = process.env[key]
      if (typeof bare === "string") return bare
    }
  } catch {
    // process not available
  }
  return undefined
}

/**
 * Read an env var by its bare name. Returns undefined if not set anywhere.
 *
 * Lookup order:
 *   1. Vite: import.meta.env.VITE_<KEY>
 *   2. Next / Node: process.env.NEXT_PUBLIC_<KEY>
 *   3. Node: process.env.<KEY>
 */
export function readEnv(key: string): EnvValue {
  return readViteEnv(key) ?? readProcessEnv(key)
}

/** Same as readEnv but throws when the value isn't set. */
export function readEnvOrThrow(key: string): string {
  const v = readEnv(key)
  if (!v) throw new Error(`Missing env var: ${key} (checked VITE_${key} and NEXT_PUBLIC_${key})`)
  return v
}
